import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { userApi } from '../../services/api'
import toast from 'react-hot-toast'

export default function UserEdit() {
    const { id } = useParams()
    const navigate = useNavigate()
    const isNew = id === 'new'

    const [loading, setLoading] = useState(!isNew)
    const [saving, setSaving] = useState(false)
    const [form, setForm] = useState({
        userName: '',
        fullName: '',
        email: '',
        initials: '',
        pin: '',
        password: '',
        isSysAdmin: false,
        canInventory: false,
        canManageTimesheets: false,
        canPackingSlipEmail: false,
        canSigCopy: false,
        canUsers: false,
        canJobList: false,
    })

    useEffect(() => {
        if (!isNew) {
            loadUser()
        }
    }, [id])

    async function loadUser() {
        try {
            const response = await userApi.getById(id)
            if (response.success && response.user) {
                setForm({
                    userName: response.user.userName || '',
                    fullName: response.user.fullName || '',
                    email: response.user.email || '',
                    initials: response.user.initials || '',
                    password: '',
                    pin: '',
                    isSysAdmin: response.user.isSysAdmin || false,
                    canPackingSlipEmail: response.user.canPackingSlipEmail || false,
                    canSigCopy: response.user.canSigCopy || false,
                    canInventory: response.user.canInventory || false,
                    canUsers: response.user.canUsers || false,
                    canManageTimesheets: response.user.canManageTimesheets || false,
                    canJobList: response.user.canJobList || false,
                })
            }
        } catch (error) {
            toast.error('Failed to load user')
            navigate('/admin/users')
        }
        setLoading(false)
    }

    function handleChange(field, value) {
        setForm(prev => ({ ...prev, [field]: value }))
    }

    const permissionFields = [
        'canInventory', 'canManageTimesheets', 'canPackingSlipEmail',
        'canSigCopy', 'canUsers', 'canJobList'
    ]

    function handleSelectAll(checked) {
        const updates = {}
        permissionFields.forEach(f => updates[f] = checked)
        setForm(prev => ({ ...prev, ...updates }))
    }

    async function handleSave() {
        if (!form.userName.trim()) {
            toast.error('Username is required')
            return
        }

        setSaving(true)
        try {
            let response
            if (isNew) {
                response = await userApi.create(form)
            } else {
                response = await userApi.update(id, form)
            }

            if (response.success) {
                toast.success(isNew ? 'User created' : 'User updated')
                navigate('/admin/users')
            } else {
                toast.error(response.message || 'Failed to save')
            }
        } catch (error) {
            toast.error('Error saving user')
        }
        setSaving(false)
    }

    async function handleDelete() {
        if (!confirm('Are you sure you want to delete this user?')) return

        setSaving(true)
        try {
            const response = await userApi.delete(id)
            if (response.success) {
                toast.success('User deleted')
                navigate('/admin/users')
            } else {
                toast.error(response.message || 'Failed to delete')
            }
        } catch (error) {
            toast.error('Error deleting user')
        }
        setSaving(false)
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner w-8 h-8"></div>
            </div>
        )
    }

    const allChecked = permissionFields.every(f => form[f])

    return (
        <div>
            <div className="page-header">
                <h1 className="page-title">{isNew ? 'New User' : 'Edit User'}</h1>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-group">
                        <label className="form-label">Username *</label>
                        <input
                            type="text"
                            value={form.userName}
                            onChange={(e) => handleChange('userName', e.target.value)}
                            className="mt-1"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Full Name</label>
                        <input
                            type="text"
                            value={form.fullName}
                            onChange={(e) => handleChange('fullName', e.target.value)}
                            className="mt-1"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Email</label>
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            className="mt-1"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Initials</label>
                        <input
                            type="text"
                            value={form.initials}
                            onChange={(e) => handleChange('initials', e.target.value)}
                            className="mt-1"
                            maxLength={5}
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            {isNew ? 'Password *' : 'Password (leave blank to keep)'}
                        </label>
                        <input
                            type="password"
                            value={form.password}
                            onChange={(e) => handleChange('password', e.target.value)}
                            className="mt-1"
                            required={isNew}
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            {isNew ? 'PIN' : 'PIN (leave blank to keep)'}
                        </label>
                        <input
                            type="text"
                            value={form.pin}
                            onChange={(e) => handleChange('pin', e.target.value)}
                            className="mt-1"
                            maxLength={10}
                        />
                    </div>
                </div>

                {/* System Admin */}
                <div className="mt-6 pt-4 border-t">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.isSysAdmin}
                            onChange={(e) => handleChange('isSysAdmin', e.target.checked)}
                            className="w-5 h-5"
                        />
                        <span className="font-semibold text-gray-800">System Admin</span>
                        <span className="text-sm text-gray-500">(Full access to everything)</span>
                    </label>
                </div>

                {/* Feature Permissions */}
                <div className="mt-6 pt-4 border-t">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium">Feature Permissions</h3>
                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
                            <input
                                type="checkbox"
                                checked={allChecked}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                className="w-4 h-4"
                            />
                            <span>Select All</span>
                        </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.canInventory || false}
                                onChange={(e) => handleChange('canInventory', e.target.checked)}
                                className="w-4 h-4"
                            />
                            <span>Inventory</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.canManageTimesheets || false}
                                onChange={(e) => handleChange('canManageTimesheets', e.target.checked)}
                                className="w-4 h-4"
                            />
                            <span>Manage Timesheets</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.canPackingSlipEmail || false}
                                onChange={(e) => handleChange('canPackingSlipEmail', e.target.checked)}
                                className="w-4 h-4"
                            />
                            <span>Email Packing Slip</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.canSigCopy || false}
                                onChange={(e) => handleChange('canSigCopy', e.target.checked)}
                                className="w-4 h-4"
                            />
                            <span>Signature Copy</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.canUsers || false}
                                onChange={(e) => handleChange('canUsers', e.target.checked)}
                                className="w-4 h-4"
                            />
                            <span>Users</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.canJobList || false}
                                onChange={(e) => handleChange('canJobList', e.target.checked)}
                                className="w-4 h-4"
                            />
                            <span>Job Numbers</span>
                        </label>
                    </div>
                </div>

                {/* Buttons */}
                <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t">
                    <button onClick={handleSave} disabled={saving} className="btn btn-primary">
                        {saving ? 'Saving...' : 'Save'}
                    </button>

                    {!isNew && (
                        <button onClick={handleDelete} disabled={saving} className="btn btn-danger">
                            Delete
                        </button>
                    )}

                    <button onClick={() => navigate('/admin/users')} className="btn btn-secondary">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    )
}